import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  Unlock,
  MapPin, 
  Plus, 
  Mail, 
  User as UserIcon, 
  Phone, 
  Instagram, 
  Trash2, 
  CheckCircle2, 
  X,
  ShieldAlert,
  Save,
  Globe,
  Trophy as TrophyIcon,
  AlertTriangle,
  Settings2,
  MessageCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { RankingService } from '../../services/rankingService';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, where, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { storage } from '../../lib/firebase';
import { UserProfile, Config, RankingConfig } from '../../types';
import firebaseConfig from '../../../firebase-applet-config.json';
import { SuperAdminWhatsApp } from '../superadmin/SuperAdminWhatsApp';

interface SuperAdminPanelProps {
  config: Config;
  db: any;
  getCollectionPath: (col: string) => string;
  userId: string;
}

export function SuperAdminPanel({ config, db, getCollectionPath, userId }: SuperAdminPanelProps) {
  const [sedes, setSedes] = useState<UserProfile[]>([]);
  const [pendingSedes, setPendingSedes] = useState<any[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [adminName, setAdminName] = useState('');
  const [adminPhotoUrl, setAdminPhotoUrl] = useState('');
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<'sedes' | 'whatsapp'>('sedes');

  // Client-side image compression helper
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400;
          const MAX_HEIGHT = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress as WEBP to preserve transparency
          const dataUrl = canvas.toDataURL('image/webp', 0.8);
          resolve(dataUrl);
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  useEffect(() => {
    async function loadAdminData() {
      try {
        const docSnap = await getDoc(doc(db, getCollectionPath('users'), userId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAdminName(data.name || '');
          setAdminPhotoUrl(data.photoUrl || '');
        }
      } catch (err) {
        console.error('Error loading admin profile:', err);
      }
    }
    loadAdminData();
  }, [userId, db, getCollectionPath]);

  const [newSede, setNewSede] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    location: '',
    instagram: '',
    address: ''
  });

  useEffect(() => {
    const qSedes = query(collection(db, getCollectionPath('users')), where('role', '==', 'admin'));
    const unsubSedes = onSnapshot(qSedes, (snapshot) => {
      setSedes(snapshot.docs.map(d => ({ ...d.data() } as UserProfile)));
    });

    const qPending = collection(db, getCollectionPath('pre_registered_sedes'));
    const unsubPending = onSnapshot(qPending, (snapshot) => {
      setPendingSedes(snapshot.docs.map(d => ({ ...d.data() } as any)));
    });

    return () => {
      unsubSedes();
      unsubPending();
    };
  }, [db, getCollectionPath]);

  const handleCreateSede = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const emailKey = newSede.email.toLowerCase().trim();
      
      // Save pre-registered Profile in Firestore
      await setDoc(doc(db, getCollectionPath('pre_registered_sedes'), emailKey), {
        email: emailKey,
        name: newSede.name,
        role: 'admin',
        phone: newSede.phone,
        location: newSede.location,
        instagram: newSede.instagram,
        address: newSede.address,
        createdAt: Date.now()
      });

      setSuccess(`Sede pre-registrada correctamente. PÃ­dele al administrador que ingrese con Google usando su correo: ${emailKey}`);
      setIsAdding(false);
      setNewSede({
        name: '',
        email: '',
        password: '',
        phone: '',
        location: '',
        instagram: '',
        address: ''
      });
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Error al pre-registrar la sede.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSede = async (uid: string) => {
    try {
      await deleteDoc(doc(db, getCollectionPath('users'), uid));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePending = async (email: string) => {
    try {
      await deleteDoc(doc(db, getCollectionPath('pre_registered_sedes'), email.toLowerCase().trim()));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleTestMode = async (sedeId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, getCollectionPath('users'), sedeId), {
        isHidden: !currentStatus
      });
      // The local state should update automatically via the snapshot listener
    } catch (err: any) {
      console.error('Error toggling test mode:', err);
      alert('Error al cambiar el estado de visibilidad del club.');
    }
  };

  const handleSuspendSede = async (sedeId: string, duration: string) => {
    let msToAdd = 0;
    let label = '';
    if (duration === '1_day') {
      msToAdd = 24 * 60 * 60 * 1000;
      label = '1 dÃ­a';
    } else if (duration === '3_days') {
      msToAdd = 3 * 24 * 60 * 60 * 1000;
      label = '3 dÃ­as';
    } else if (duration === '1_week') {
      msToAdd = 7 * 24 * 60 * 60 * 1000;
      label = '1 semana';
    } else if (duration === '1_month') {
      msToAdd = 30 * 24 * 60 * 60 * 1000;
      label = '1 mes';
    } else if (duration === 'forever') {
      msToAdd = -1;
      label = 'tiempo indefinido';
    }

    const until = msToAdd === -1 ? null : Date.now() + msToAdd;
    const reason = `InhabilitaciÃ³n de cuenta por ${label}`;

    try {
      await updateDoc(doc(db, getCollectionPath('users'), sedeId), {
        isSuspended: true,
        suspendedUntil: until,
        suspensionReason: reason,
      });

      const notifRef = doc(collection(db, getCollectionPath('notifications')));
      await setDoc(notifRef, {
        id: notifRef.id,
        userId: sedeId,
        type: 'warning',
        title: 'âs ï¸? Cuenta Inhabilitada',
        message: `Tu cuenta de sede ha sido inhabilitada por el Administrador General por ${label}. Si crees que se trata de un error, comunÃ­cate con soporte.`,
        read: false,
        createdAt: Date.now(),
      });

      setSuccess(`Sede inhabilitada por ${label}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      alert('Error al inhabilitar la sede');
    }
  };

  const handleLiftSedeSuspension = async (sedeId: string) => {
    try {
      await updateDoc(doc(db, getCollectionPath('users'), sedeId), {
        isSuspended: false,
        suspendedUntil: null,
        suspensionReason: '',
      });

      const notifRef = doc(collection(db, getCollectionPath('notifications')));
      await setDoc(notifRef, {
        id: notifRef.id,
        userId: sedeId,
        type: 'warning',
        title: 'âo. Cuenta Habilitada',
        message: 'Â¡Tu cuenta de sede ha sido reactivada! Ya puedes acceder a todas las funciones de administraciÃ³n.',
        read: false,
        createdAt: Date.now(),
      });

      setSuccess('Sede habilitada correctamente.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      alert('Error al habilitar la sede');
    }
  };

  const handleChangePlan = async (sedeId: string, plan: 'Basico' | 'Full' | 'Full Plus') => {
    try {
      await updateDoc(doc(db, getCollectionPath('users'), sedeId), {
        subscriptionPlan: plan
      });
      setSuccess(`Plan actualizado a ${plan}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      alert('Error al actualizar el plan');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-in slide-in-from-right-4 p-2 md:p-0">
      
      {/* 1. Perfil del Super Admin (LIGHTER BACKGROUND & TEXT STANDOUT) */}
      <div className="bg-white/[0.08] p-5 md:p-6 rounded-2xl border border-white/15 shadow-xl relative overflow-hidden backdrop-blur-md">
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-theme-primary/10 blur-2xl rounded-full pointer-events-none" />
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start relative z-10">
          
          {/* Logo compact uploader */}
          <div className="flex flex-col items-center shrink-0">
            <div className="w-24 h-24 rounded-full border-2 theme-primary-border mb-2 overflow-hidden relative group bg-black/40 flex items-center justify-center shadow-lg transition-all hover:scale-[1.02]">
              {adminPhotoUrl ? (
                <img src={adminPhotoUrl} alt="Logo" className="w-full h-full object-cover group-hover:opacity-45 transition-all" />
              ) : (
                <UserIcon size={24} className="theme-primary-text opacity-70" />
              )}
              <label className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer bg-black/70 transition-all">
                <span className="text-[9px] font-black uppercase tracking-wider text-white text-center px-2">Cambiar</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const base64 = await compressImage(file);
                      setAdminPhotoUrl(base64);
                    } catch (err) {
                      console.error(err);
                      alert('Error al procesar la imagen.');
                    }
                  }} 
                />
              </label>
            </div>
            <p className="text-[8px] opacity-40 font-bold uppercase tracking-widest text-center text-white">Logo Cuadrado</p>
          </div>

          {/* Name & Save button */}
          <div className="flex-1 w-full space-y-3">
            <div>
              <label className="block text-[9px] font-black uppercase opacity-60 mb-1.5 ml-1 tracking-wider text-white">Nombre del SÃºper Admin / Circuito</label>
              <input
                type="text"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                placeholder="Ej: Roca Padel Tour"
                className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl px-3.5 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-sm text-white placeholder:text-white/30"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={async () => {
                  setSavingAdmin(true);
                  try {
                    let finalPhotoUrl = adminPhotoUrl;
                    if (finalPhotoUrl.startsWith('data:image/')) {
                      const imageRef = ref(storage, `users/${userId}_photo_${Date.now()}.jpg`);
                      await uploadString(imageRef, finalPhotoUrl, 'data_url');
                      finalPhotoUrl = await getDownloadURL(imageRef);
                    }
                    await updateDoc(doc(db, getCollectionPath('users'), userId), {
                      name: adminName,
                      photoUrl: finalPhotoUrl
                    });
                    setSuccess('Perfil de SÃºper Admin actualizado correctamente.');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err: any) {
                    console.error(err);
                    alert('Error al guardar el perfil: ' + err.message);
                  } finally {
                    setSavingAdmin(false);
                  }
                }}
                disabled={savingAdmin}
                className="px-4 py-2 bg-theme-primary/10 hover:bg-theme-primary/20 border theme-primary-border theme-primary-text rounded-xl text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
              >
                {savingAdmin ? (
                  <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={12} /> Guardar Perfil
                  </>
                )}
              </button>
              
              <span className="text-[9px] opacity-60 uppercase tracking-widest flex items-center gap-1.5 font-black text-white">
                <Settings2 size={10} className="theme-primary-text" /> Cuenta Principal Master
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-center border-b border-white/5 pb-6">
        <div className="flex flex-wrap justify-center items-center gap-1.5 bg-black/40 border border-white/10 rounded-2xl p-1.5 w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('sedes')}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeTab === 'sedes'
                ? 'bg-blue-500 text-white shadow-[0_4px_15px_rgba(59,130,246,0.4)] scale-[1.02]'
                : 'text-white/50 hover:text-white hover:bg-white/10'
            }`}
          >
            <MapPin size={14} className="shrink-0" />
            <span>Sedes</span>
          </button>

          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
              activeTab === 'whatsapp'
                ? 'bg-[#25D366] text-black shadow-[0_4px_15px_rgba(37,211,102,0.4)] scale-[1.02]'
                : 'text-white/50 hover:text-white hover:bg-white/10'
            }`}
          >
            <MessageCircle size={14} className="shrink-0" />
            <span>WhatsApp Master</span>
          </button>
        </div>
      </div>

      {activeTab === 'whatsapp' ? (
        <SuperAdminWhatsApp db={db} getCollectionPath={getCollectionPath} />
      ) : (
      <div className="grid grid-cols-1 gap-6 items-start">
        
        <div className="space-y-6">
          
          {/* Header Card (LIGHTER BACKGROUND & POPPING CONTRAST) */}
          <div className="bg-white/[0.08] p-5 rounded-2xl border border-white/15 relative overflow-hidden backdrop-blur-md flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
            <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none">
              <Globe size={100} />
            </div>
            <div className="relative z-10">
              <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2 theme-primary-text">
                <Globe size={16} className="animate-pulse" /> Sedes Oficiales Autorizadas
              </h3>
              <p className="opacity-60 text-white text-[10px] uppercase tracking-wider mt-1 font-bold">
                Controla los clubes y sedes con permiso para organizar torneos.
              </p>
            </div>
            <button
              onClick={() => setIsAdding(!isAdding)}
              className="theme-primary-bg text-black font-black px-4 py-2 rounded-xl transition-all shadow-md hover:scale-105 flex items-center justify-center gap-1.5 uppercase tracking-widest text-[10px] shrink-0 relative z-10 self-start sm:self-auto cursor-pointer"
            >
              {isAdding ? <X size={12} /> : <Plus size={12} />}
              {isAdding ? 'Cancelar' : 'Nueva Sede'}
            </button>
          </div>

          {/* Formulario de CreaciÃ³n (Brighter & High Contrast) */}
          {isAdding && (
            <div className="bg-white/[0.12] backdrop-blur-2xl rounded-2xl border border-white/20 p-5 shadow-2xl relative animate-in zoom-in-95 duration-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-theme-primary/10 rounded-xl theme-primary-text">
                  <ShieldAlert size={16} />
                </div>
                <h4 className="text-xs font-black uppercase tracking-wider theme-primary-text">Nueva Cuenta de Sede</h4>
              </div>

              <form onSubmit={handleCreateSede} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">Nombre Sede / Club</label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 theme-primary-text opacity-50" size={14} />
                      <input
                        required
                        type="text"
                        value={newSede.name}
                        onChange={(e) => setNewSede({...newSede, name: e.target.value})}
                        placeholder="Ej: Club Padel Arena"
                        className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl pl-9 pr-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-xs text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">Email Acceso</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 theme-primary-text opacity-50" size={14} />
                      <input
                        required
                        type="email"
                        value={newSede.email}
                        onChange={(e) => setNewSede({...newSede, email: e.target.value})}
                        placeholder="club@ejemplo.com"
                        className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl pl-9 pr-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-xs text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">TelÃ©fono</label>
                      <input
                        type="text"
                        value={newSede.phone}
                        onChange={(e) => setNewSede({...newSede, phone: e.target.value})}
                        placeholder="NÂº TelÃ©fono"
                        className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl px-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-xs text-white placeholder:text-white/30"
                      />
                    </div>
                    <div>
                      <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">Instagram</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-extrabold text-white/85 select-none">
                          @
                        </span>
                        <input
                          type="text"
                          placeholder="tu usuario"
                          value={newSede.instagram}
                          onChange={(e) => {
                            let val = e.target.value.trim();
                            if (val.startsWith('@')) {
                              val = val.substring(1);
                            }
                            if (val.includes('instagram.com/')) {
                              const parts = val.split('instagram.com/');
                              if (parts.length > 1) {
                                val = parts[1].split('/')[0].split('?')[0];
                              }
                            }
                            setNewSede({...newSede, instagram: val});
                          }}
                          className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl pl-7 pr-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-xs text-white placeholder:text-white/30"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">DirecciÃ³n FÃ­sica</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 theme-primary-text opacity-50" size={14} />
                      <input
                        type="text"
                        value={newSede.address}
                        onChange={(e) => setNewSede({...newSede, address: e.target.value})}
                        placeholder="Calle, NÃºmero, Ciudad"
                        className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl pl-9 pr-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-xs text-white placeholder:text-white/30"
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 space-y-3 pt-2">
                  <div>
                    <label className="block text-[8px] font-black uppercase opacity-60 mb-1 ml-0.5 text-white">Enlace UbicaciÃ³n (Google Maps)</label>
                    <input
                      type="text"
                      value={newSede.location}
                      onChange={(e) => setNewSede({...newSede, location: e.target.value})}
                      placeholder="https://maps.google.com/..."
                      className="w-full bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl px-3 py-2.5 focus:theme-primary-border outline-none transition-all font-bold text-[10px] text-white placeholder:text-white/30"
                    />
                  </div>

                  {error && <p className="text-red-400 text-[11px] font-bold bg-red-400/5 px-3 py-2 rounded-xl border border-red-400/10 text-center">{error}</p>}
                  
                  <button
                    disabled={loading}
                    className="w-full theme-primary-bg text-black font-black py-3 rounded-xl transition-all shadow-md hover:scale-[1.01] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 uppercase tracking-wider text-[10px] cursor-pointer"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={14} /> Confirmar Nueva Sede
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Registros Pendientes (LIGHTER & SHINING CONTAINER) */}
          {pendingSedes.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <Lock className="theme-primary-text opacity-70" size={12} />
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-white opacity-70 italic">Registros Pendientes ({pendingSedes.length})</h4>
                <div className="h-px flex-1 bg-white/10 ml-3"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pendingSedes.map((p) => (
                  <div key={p.email} className="bg-white/[0.08] border border-dashed border-theme-primary/30 p-4 rounded-xl relative group flex flex-col justify-between shadow-md">
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-[10px] font-black uppercase tracking-tight text-white truncate max-w-[80%]">{p.name}</span>
                        {confirmDeleteId === p.email ? (
                          <div className="flex bg-red-500/10 border border-red-500 text-red-500 rounded-lg overflow-hidden shadow-md z-10">
                            <button onClick={() => handleDeletePending(p.email)} className="px-2 py-0.5 bg-red-500 text-white font-black text-[9px] uppercase cursor-pointer">SÃ­</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-0.5 hover:bg-white/5 font-black text-[9px] text-white/70 cursor-pointer">No</button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => setConfirmDeleteId(p.email)}
                            className="p-1 text-red-400 hover:bg-red-500/10 rounded-lg transition-all cursor-pointer"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] theme-primary-text font-black truncate">{p.email}</p>
                    </div>
                    <div className="text-[8px] uppercase tracking-widest font-black text-white/50 mt-3 flex items-center gap-1.5 border-t border-white/10 pt-2">
                      <div className="w-1.5 h-1.5 bg-theme-primary rounded-full animate-pulse"></div>
                      Esperando login oficial
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lista de Sedes Oficiales Registradas (LIGHTER BACKGROUND CARDS WITH POPPING BORDERS) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Globe className="theme-primary-text opacity-70" size={12} />
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-white opacity-70 italic">Clubes Autorizados ({sedes.length})</h4>
              <div className="h-px flex-1 bg-white/10 ml-3"></div>
            </div>
            
            {sedes.length === 0 && !isAdding && (
              <div className="text-center py-10 bg-white/[0.05] rounded-2xl border border-dashed border-white/10">
                <Globe className="mx-auto opacity-20 mb-2 theme-primary-text" size={32} />
                <p className="opacity-40 font-bold uppercase tracking-widest text-[10px] text-white italic">No hay sedes autorizadas</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sedes.map((sede) => {
                const isSedeSuspended = !!(sede.isSuspended && (sede.suspendedUntil === null || sede.suspendedUntil > Date.now()));
                
                return (
                  <div key={sede.uid} className={`bg-white/[0.08] border p-4 rounded-xl shadow-lg relative group transition-all flex flex-col justify-between ${isSedeSuspended ? 'border-red-500/30 bg-red-950/10' : sede.isHidden ? 'border-yellow-500/30 bg-yellow-950/10 opacity-80' : 'border-white/10 hover:theme-primary-border'}`}>
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 transition-colors ${isSedeSuspended ? 'bg-red-500/10 border-red-500/20 text-red-400' : sede.isHidden ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' : 'bg-white/10 border-white/15 group-hover:bg-theme-primary/10'}`}>
                            <MapPin className={isSedeSuspended ? 'text-red-400' : sede.isHidden ? 'text-yellow-400' : 'theme-primary-text'} size={16} />
                          </div>
                          <div className="min-w-0">
                            <h5 className="text-xs font-black uppercase tracking-tight truncate text-white">{sede.name}</h5>
                            <span className="text-[9px] font-bold text-white/60 truncate block">{sede.email}</span>
                          </div>
                        </div>

                        {confirmDeleteId === sede.uid ? (
                          <div className="flex bg-red-500/10 border border-red-500 text-red-500 rounded-lg overflow-hidden shadow-md shrink-0 z-10">
                            <button onClick={() => handleDeleteSede(sede.uid)} className="px-2 py-0.5 bg-red-500 text-white font-black text-[9px] uppercase cursor-pointer">SÃ­</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="px-2 py-0.5 hover:bg-white/5 font-black text-[9px] text-white/70 cursor-pointer">No</button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => setConfirmDeleteId(sede.uid)}
                            className="p-1 bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 transition-all rounded-lg hover:bg-red-500 hover:text-white shrink-0 cursor-pointer"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>

                      {sede.phone && (
                        <div className="flex items-center gap-1.5 text-[10px] opacity-60 ml-0.5 text-white font-bold">
                          <Phone size={10} className={isSedeSuspended ? 'text-red-400/70' : 'theme-primary-text opacity-70'} />
                          <span>{sede.phone}</span>
                        </div>
                      )}

                      <div className="mt-2 text-[10px] flex items-center gap-2 ml-0.5">
                        <span className="opacity-60 text-white font-bold uppercase tracking-widest text-[8px]">Plan:</span>
                        <select
                          value={sede.subscriptionPlan || 'Basico'}
                          onChange={(e) => handleChangePlan(sede.uid, e.target.value as any)}
                          className="bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-black text-theme-primary outline-none cursor-pointer"
                        >
                          <option value="Basico" className="bg-neutral-900 text-white">BÃ¡sico</option>
                          <option value="Full" className="bg-neutral-900 text-white">Full</option>
                          <option value="Full Plus" className="bg-neutral-900 text-white">Full Plus</option>
                        </select>
                      </div>

                      {isSedeSuspended && (
                        <div className="mt-2 text-[9px] bg-red-500/10 text-red-400 px-2 py-1 rounded-lg border border-red-500/25 flex items-center gap-1 font-bold animate-pulse">
                          <AlertTriangle size={10} className="shrink-0" />
                          <span>Inhabilitado {sede.suspendedUntil ? `hasta ${new Date(sede.suspendedUntil).toLocaleDateString()}` : 'indefinidamente'}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                        <span className="text-[8px] font-black uppercase tracking-widest text-white/50 px-1.5 py-0.5 bg-white/10 rounded">SEDE OFICIAL</span>
                        {sede.isHidden && (
                          <span className="text-[8px] font-black uppercase tracking-widest text-yellow-400 px-1.5 py-0.5 bg-yellow-500/10 border border-yellow-500/20 rounded">OCULTO / PRUEBA</span>
                        )}
                      </div>
                      
                      {/* Suspension & Visibility UI logic */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => handleToggleTestMode(sede.uid, !!sede.isHidden)}
                          className={`p-1 rounded transition-all cursor-pointer ${sede.isHidden ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/70'}`}
                          title={sede.isHidden ? 'Hacer visible' : 'Ocultar (Modo Prueba)'}
                        >
                          {sede.isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        
                        {isSedeSuspended ? (
                          <button
                            type="button"
                            onClick={() => handleLiftSedeSuspension(sede.uid)}
                            className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tight transition-all flex items-center gap-1 cursor-pointer"
                            title="Habilitar Sede"
                          >
                            <Unlock size={8} /> Habilitar
                          </button>
                        ) : (
                          <div className="relative">
                            <select
                              value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleSuspendSede(sede.uid, e.target.value);
                              }
                            }}
                            className="appearance-none bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tight transition-all cursor-pointer text-center outline-none select-none"
                          >
                            <option value="" disabled className="bg-neutral-900 text-red-400 font-bold uppercase text-[8px]">Inhabilitar â-¾</option>
                            <option value="1_day" className="bg-neutral-900 text-white font-normal">1 dÃ­a</option>
                            <option value="3_days" className="bg-neutral-900 text-white font-normal">3 dÃ­as</option>
                            <option value="1_week" className="bg-neutral-900 text-white font-normal">1 semana</option>
                            <option value="1_month" className="bg-neutral-900 text-white font-normal">1 mes</option>
                            <option value="forever" className="bg-neutral-900 text-white font-normal">Para siempre</option>
                          </select>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </div>
      )}

      {success && (
        <div className="fixed bottom-6 right-6 bg-emerald-500 text-black px-5 py-3 rounded-xl font-black shadow-2xl flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-3 text-xs z-50">
           <CheckCircle2 size={18} />
           {success}
        </div>
      )}
    </div>
  );
}
